import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { useEdiPurchaseOrderCreate, useEdiPurchaseOrderDetail } from '@/api/hanly-v2/edi/edi';
import type { EdiPurchaseOrderDetailResponse } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { MobilePageHeader } from '@/components/mobile-page-header';
import { useShowPrices } from '@/contexts/auth';
import { toCatalogItemDisplayData } from '@/features/orders/display-data';
import { OrderConfirmModal } from '@/features/orders/order-confirm-modal';
import { OrderFormBody } from '@/features/orders/order-form-body';
import { buildReorderPlan, type ReorderPlan } from '@/features/orders/reorder';
import { type OrderCreateForm, orderCreateFormSchema } from '@/features/orders/schema';
import { calculateOrderTotals } from '@/features/orders/types';
import { useAllCatalogItems } from '@/features/orders/use-all-catalog-items';
import { formatYMDSlash } from '@/utils/date';

interface ReorderNoticeProps {
  source: EdiPurchaseOrderDetailResponse;
  plan: ReorderPlan;
  showPrices: boolean;
}

const ReorderNotice = ({ source, plan, showPrices }: ReorderNoticeProps) => (
  <div className="flex flex-col gap-1.5 border border-neutral-200 bg-white px-4 py-3 text-sm">
    <p className="flex gap-2">
      <InformationCircleIcon className="mt-0.5 size-5 shrink-0 text-sky-800" />
      <span>
        <span className="font-bold">
          {formatYMDSlash(source.created_at)} の注文（EDI No. {source.id}）
        </span>
        の内容をコピーしました。{showPrices && '単価は現在の価格で計算しています。'}
      </span>
    </p>
    {plan.unavailableNames.length > 0 && (
      <p className="flex gap-2 text-amber-700">
        <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
        <span>
          {plan.unavailableNames.length}品目は現在の商品カタログに無いため追加できませんでした（
          {plan.unavailableNames.join('、')}）
        </span>
      </p>
    )}
  </div>
);

export const OrderNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceOrderId = Number(searchParams.get('from')) || null;
  const showPrices = useShowPrices();

  const { data: catalogData, isLoading: isCatalogLoading } = useAllCatalogItems();
  const catalogItems = useMemo(() => catalogData?.results ?? [], [catalogData?.results]);
  const itemDisplayColumns = catalogData?.itemDisplayColumns;

  const { data: sourceOrder, isLoading: isSourceLoading } = useEdiPurchaseOrderDetail(sourceOrderId ?? 0, {
    query: { enabled: sourceOrderId !== null },
  });

  const reorderPlan = useMemo(
    () =>
      sourceOrder
        ? buildReorderPlan(sourceOrder.items, new Set(catalogItems.map((ci) => ci.product_variation.id)))
        : null,
    [sourceOrder, catalogItems],
  );

  const createMutation = useEdiPurchaseOrderCreate();

  const formValues = useMemo<OrderCreateForm | undefined>(
    () =>
      catalogItems.length > 0
        ? {
            deliveryMode: 'date',
            desiredDeliveryDate: '',
            notes: '',
            items: catalogItems.map((item) => ({
              product_variation_id: item.product_variation.id,
              quantity: reorderPlan?.quantities.get(item.product_variation.id) ?? 0,
              desired_delivery_date: null,
            })),
          }
        : undefined,
    [catalogItems, reorderPlan],
  );

  const methods = useForm<OrderCreateForm>({
    resolver: zodResolver(orderCreateFormSchema),
    values: formValues,
  });

  const watchedItems = useWatch({ control: methods.control, name: 'items' });
  const watchedNotes = useWatch({ control: methods.control, name: 'notes' });

  const displayItems = useMemo(
    () =>
      catalogItems.map((ci, index) =>
        toCatalogItemDisplayData(
          ci,
          watchedItems?.[index]?.quantity || 0,
          watchedItems?.[index]?.desired_delivery_date ?? null,
          reorderPlan?.previousUnitPrices.get(ci.product_variation.id) ?? null,
        ),
      ),
    [catalogItems, watchedItems, reorderPlan],
  );

  const totals = useMemo(() => calculateOrderTotals(displayItems), [displayItems]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const watchedDeliveryMode = useWatch({ control: methods.control, name: 'deliveryMode' });
  const watchedDeliveryDate = useWatch({ control: methods.control, name: 'desiredDeliveryDate' });

  const deliveryDateLabel = useMemo(() => {
    if (watchedDeliveryMode === 'asap') return '即納希望';
    return watchedDeliveryDate ? formatYMDSlash(watchedDeliveryDate) : '-';
  }, [watchedDeliveryMode, watchedDeliveryDate]);

  const handleOpenConfirmModal = methods.handleSubmit((formData) => {
    const itemsWithQuantity = formData.items.filter((item) => item.quantity > 0);
    if (itemsWithQuantity.length === 0) {
      toast.error('数量が1以上の商品を追加してください');
      return;
    }
    setIsConfirmModalOpen(true);
  });

  const handleConfirm = async () => {
    const formData = methods.getValues();

    try {
      const res = await createMutation.mutateAsync({
        data: {
          desired_delivery_date:
            formData.deliveryMode === 'date' && formData.desiredDeliveryDate
              ? new Date(`${formData.desiredDeliveryDate}T00:00:00`).toISOString()
              : null,
          notes: formData.notes,
          items: formData.items.map((item) => ({
            product_variation_id: item.product_variation_id,
            quantity: String(item.quantity),
            desired_delivery_date: item.desired_delivery_date,
          })),
        },
      });
      toast.success('注文が完了しました');
      setIsConfirmModalOpen(false);
      navigate(`/orders/${res.id}`);
    } catch {
      toast.error('エラーが発生しました');
    }
  };

  // コピー元の数量を入れた状態で初期化するため、コピー元の取得を待ってからフォームを出す
  if (isCatalogLoading || isSourceLoading) {
    return <div className="py-8 text-center text-neutral-600">読み込み中...</div>;
  }

  return (
    <div className="flex-1 bg-neutral-50 md:bg-white">
      <MobilePageHeader title="新規発注" backTo="/orders" />
      <Breadcrumbs
        items={[{ label: 'ホーム', to: '/' }, { label: '発注', to: '/orders' }, { label: '新規作成' }]}
      />

      <div className="mx-auto max-w-[1440px] px-3 pt-3 md:px-8 md:pt-4 md:pb-10">
        <FormProvider {...methods}>
          <form onSubmit={(e) => e.preventDefault()}>
            <OrderFormBody
              items={displayItems}
              itemDisplayColumns={itemDisplayColumns}
              totals={totals}
              onConfirmClick={handleOpenConfirmModal}
              emptyMessage="商品カタログに商品が登録されていません"
              initialFilter={reorderPlan && reorderPlan.quantities.size > 0 ? 'entered' : 'all'}
              notice={
                sourceOrder && reorderPlan ? (
                  <ReorderNotice source={sourceOrder} plan={reorderPlan} showPrices={showPrices} />
                ) : sourceOrderId !== null ? (
                  <p className="border border-neutral-200 bg-white px-4 py-3 text-sm text-red-600">
                    コピー元の注文を取得できませんでした
                  </p>
                ) : undefined
              }
            />

            <OrderConfirmModal
              isOpen={isConfirmModalOpen}
              isCreate
              onClose={() => setIsConfirmModalOpen(false)}
              onConfirm={handleConfirm}
              isLoading={createMutation.isPending}
              deliveryDateLabel={deliveryDateLabel}
              notes={watchedNotes ?? ''}
              items={displayItems}
              itemDisplayColumns={itemDisplayColumns ?? []}
              totals={totals}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
